import type { LikertScaleOptionCount, LikertScaleType } from './blocks';

export const LIKERT_SCALE_OPTIONS = [
  { value: 'agreement', label: 'Agreement' },
  { value: 'confidence', label: 'Confidence' },
  { value: 'importance', label: 'Importance' },
  { value: 'likelihood', label: 'Likelihood' },
  { value: 'frequency', label: 'Frequency' },
  { value: 'satisfaction', label: 'Satisfaction' },
] as const satisfies ReadonlyArray<{ value: LikertScaleType; label: string }>;

export const LIKERT_OPTION_COUNTS = [7, 5] as const satisfies ReadonlyArray<LikertScaleOptionCount>;

const LIKERT_SCALE_DISPLAY_LABELS: Record<LikertScaleType, string> = {
  agreement: 'Agreement',
  confidence: 'Confidence',
  importance: 'Importance',
  likelihood: 'Likelihood',
  frequency: 'Frequency',
  satisfaction: 'Satisfaction',
};

const LIKERT_OPTION_LABELS_BY_ID = {
  strongly_disagree: 'Strongly Disagree',
  disagree: 'Disagree',
  somewhat_disagree: 'Somewhat Disagree',
  neutral_dont_know: "Neutral / Don't Know",
  somewhat_agree: 'Somewhat Agree',
  agree: 'Agree',
  strongly_agree: 'Strongly Agree',
  not_at_all_confident: 'Not At All Confident',
  not_confident: 'Not Confident',
  somewhat_not_confident: 'Somewhat Not Confident',
  somewhat_confident: 'Somewhat Confident',
  confident: 'Confident',
  very_confident: 'Very Confident',
  not_at_all_important: 'Not At All Important',
  not_important: 'Not Important',
  somewhat_not_important: 'Somewhat Not Important',
  somewhat_important: 'Somewhat Important',
  important: 'Important',
  very_important: 'Very Important',
  very_unlikely: 'Very Unlikely',
  unlikely: 'Unlikely',
  somewhat_unlikely: 'Somewhat Unlikely',
  somewhat_likely: 'Somewhat Likely',
  likely: 'Likely',
  very_likely: 'Very Likely',
  never: 'Never',
  rarely: 'Rarely',
  occasionally: 'Occasionally',
  sometimes_dont_know: "Sometimes / Don't Know",
  often: 'Often',
  very_often: 'Very Often',
  always: 'Always',
  very_dissatisfied: 'Very Dissatisfied',
  dissatisfied: 'Dissatisfied',
  somewhat_dissatisfied: 'Somewhat Dissatisfied',
  somewhat_satisfied: 'Somewhat Satisfied',
  satisfied: 'Satisfied',
  very_satisfied: 'Very Satisfied',
} as const;

export type LikertScaleOptionId = keyof typeof LIKERT_OPTION_LABELS_BY_ID;

export interface LikertScaleOptionDescriptor {
  id: LikertScaleOptionId;
  label: string;
}

const LIKERT_OPTION_IDS: Record<
  LikertScaleType,
  Record<LikertScaleOptionCount, readonly LikertScaleOptionId[]>
> = {
  agreement: {
    7: [
      'strongly_disagree',
      'disagree',
      'somewhat_disagree',
      'neutral_dont_know',
      'somewhat_agree',
      'agree',
      'strongly_agree',
    ],
    5: [
      'strongly_disagree',
      'disagree',
      'neutral_dont_know',
      'agree',
      'strongly_agree',
    ],
  },
  confidence: {
    7: [
      'not_at_all_confident',
      'not_confident',
      'somewhat_not_confident',
      'neutral_dont_know',
      'somewhat_confident',
      'confident',
      'very_confident',
    ],
    5: [
      'not_at_all_confident',
      'not_confident',
      'neutral_dont_know',
      'confident',
      'very_confident',
    ],
  },
  importance: {
    7: [
      'not_at_all_important',
      'not_important',
      'somewhat_not_important',
      'neutral_dont_know',
      'somewhat_important',
      'important',
      'very_important',
    ],
    5: [
      'not_at_all_important',
      'not_important',
      'neutral_dont_know',
      'important',
      'very_important',
    ],
  },
  likelihood: {
    7: [
      'very_unlikely',
      'unlikely',
      'somewhat_unlikely',
      'neutral_dont_know',
      'somewhat_likely',
      'likely',
      'very_likely',
    ],
    5: [
      'very_unlikely',
      'unlikely',
      'neutral_dont_know',
      'likely',
      'very_likely',
    ],
  },
  frequency: {
    7: [
      'never',
      'rarely',
      'occasionally',
      'sometimes_dont_know',
      'often',
      'very_often',
      'always',
    ],
    5: ['never', 'rarely', 'sometimes_dont_know', 'often', 'always'],
  },
  satisfaction: {
    7: [
      'very_dissatisfied',
      'dissatisfied',
      'somewhat_dissatisfied',
      'neutral_dont_know',
      'somewhat_satisfied',
      'satisfied',
      'very_satisfied',
    ],
    5: [
      'very_dissatisfied',
      'dissatisfied',
      'neutral_dont_know',
      'satisfied',
      'very_satisfied',
    ],
  },
};

export function getLikertScaleDisplayLabel(scale: LikertScaleType): string {
  return LIKERT_SCALE_DISPLAY_LABELS[scale];
}

export function getLikertScaleOptionLabels(
  scale: LikertScaleType,
  optionCount: LikertScaleOptionCount,
): readonly string[] {
  return getLikertScaleOptionDescriptors(scale, optionCount).map((option) => option.label);
}

export function getLikertScaleOptionDescriptors(
  scale: LikertScaleType,
  optionCount: LikertScaleOptionCount,
): readonly LikertScaleOptionDescriptor[] {
  return LIKERT_OPTION_IDS[scale][optionCount].map((id) => ({
    id,
    label: LIKERT_OPTION_LABELS_BY_ID[id],
  }));
}
